import * as React from 'react';
import clsx from 'clsx';
import { Button } from '@wartek-id/button';
import { Text } from '@wartek-id/text';
import { useDropzone } from 'react-dropzone';
import { Spinner } from '@wartek-id/spinner';
import { Icon } from '@wartek-id/icon';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".FileUpload-module_description__bhKEB,.FileUpload-module_label__azBFW{margin-bottom:4px}.FileUpload-module_uploadFieldPlaceholder__p2Uq1{border-style:dashed!important}.FileUpload-module_uploadField__3lfkr{align-items:center;background:var(--color-surface-default);border:1px solid var(--color-border-default);border-radius:var(--size-border-radius-base);display:flex;justify-content:space-between;margin-bottom:4px;padding:12px 16px}.FileUpload-module_uploadField__3lfkr.FileUpload-module_error__LqYwy{background:var(--color-surface-critical-default);border-color:var(--color-border-critical-default)}.FileUpload-module_dragActive__P6K-j{background:var(--color-surface-selected-default)}.FileUpload-module_dragActive__P6K-j .FileUpload-module_uploadField__3lfkr{background:transparent}.FileUpload-module_uploadFieldText__uti-I{-webkit-box-orient:vertical;-webkit-line-clamp:1;display:-webkit-box;flex:1;margin-left:4px;overflow:hidden;padding-bottom:2px;padding-top:2px;word-break:break-all}.FileUpload-module_uploadFieldTextContainer__Wjd84{align-items:center;display:flex;flex:1}";
var styles = {"label":"FileUpload-module_label__azBFW","description":"FileUpload-module_description__bhKEB","uploadFieldPlaceholder":"FileUpload-module_uploadFieldPlaceholder__p2Uq1","uploadField":"FileUpload-module_uploadField__3lfkr","error":"FileUpload-module_error__LqYwy","dragActive":"FileUpload-module_dragActive__P6K-j","uploadFieldText":"FileUpload-module_uploadFieldText__uti-I","uploadFieldTextContainer":"FileUpload-module_uploadFieldTextContainer__Wjd84"};
styleInject(css_248z);

var FileItem = function (_a) {
    var isError = _a.isError, isUploading = _a.isUploading, isDragActive = _a.isDragActive, isPlaceholder = _a.isPlaceholder, label = _a.label, action = _a.action, handleEditButtonClick = _a.onEditClick, handleDeleteButtonClick = _a.onDeleteClick, placeholder = _a.placeholder, actionLabel = _a.actionLabel, open = _a.open, index = _a.index;
    function onClickAction(e) {
        e.stopPropagation();
        if (isPlaceholder) {
            open();
            return;
        }
        switch (action) {
            case 'delete':
                handleDeleteButtonClick === null || handleDeleteButtonClick === void 0 ? void 0 : handleDeleteButtonClick(label || '');
                break;
            case 'edit':
                handleEditButtonClick === null || handleEditButtonClick === void 0 ? void 0 : handleEditButtonClick(index);
                break;
        }
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: clsx(styles.uploadField, isError && styles.error, isPlaceholder && styles.uploadFieldPlaceholder), style: {
                cursor: isPlaceholder ? 'pointer' : 'default',
            } },
            React.createElement("div", { className: styles.uploadFieldTextContainer },
                React.createElement(Icon, { as: "i", fontSize: isPlaceholder ? 'sm' : 'default', style: {
                        transform: isPlaceholder ? 'rotate(45deg)' : 'rotate(0deg)',
                    } }, isPlaceholder ? 'attach_file' : 'text_snippet'),
                React.createElement(Text, { variant: "body", color: isPlaceholder ? 'subdued' : 'default', className: styles.uploadFieldText }, isPlaceholder
                    ? isDragActive
                        ? placeholder.dragActive
                        : placeholder.empty
                    : label)),
            !isUploading && (actionLabel['empty'] || actionLabel[action]) && (React.createElement(Button, { size: "sm", color: action === 'delete' && !isPlaceholder ? 'red' : 'black', variant: "ghost", onClick: onClickAction }, isPlaceholder ? actionLabel['empty'] : actionLabel[action])),
            isUploading && React.createElement(Spinner, { size: "sm" }))));
};

var DEFAULT_ERROR_MESSAGE = {
    exceedingMaxSize: 'Ukuran file melebihi batas maksimal 2 MB',
    exceedingMaxFiles: 'Jumlah file melebihi batas maksimal',
    invalidExtension: 'Format file tidak valid',
    invalidSize: 'Ukuran file tidak valid',
    required: 'Field harus diisi',
};
var ERROR_TYPES = {
    'file-too-large': 'exceedingMaxSize',
    'file-invalid-type': 'invalidExtension',
    'too-many-files': 'exceedingMaxFiles',
    'file-too-small': 'invalidSize',
};
var DEFAULT_ACTION_LABELS = {
    empty: undefined,
    delete: 'Hapus',
    edit: 'Ganti File',
};
var DEFAULT_PLACEHOLDER = {
    empty: 'Pilih File',
    dragActive: 'Lepas untuk mengunggah file',
};

function FileListItems(files) {
    var dt = new DataTransfer();
    for (var i = 0, len = files.length; i < len; i++)
        dt.items.add(files[i]);
    return dt.files;
}
var Fileupload = React.forwardRef(function (props, inputRef) {
    var _a;
    var _b = props.accept, accept = _b === void 0 ? ['png', 'jpg'] : _b, label = props.label, disabled = props.disabled, helperText = props.helperText, minSize = props.minSize, maxSize = props.maxSize, _c = props.maxFiles, maxFiles = _c === void 0 ? 1 : _c, name = props.name, onChange = props.onChange, onDeleteClick = props.onDeleteClick, propOnDrop = props.onDrop, propOnFileDropRejected = props.onDropRejected, defaultValue = props.defaultValue, errorState = props.errorState, isUploading = props.isUploading, className = props.className, style = props.style, _d = props.id, id = _d === void 0 ? 'fileupload' : _d, _e = props.action, action = _e === void 0 ? 'delete' : _e, _f = props.updateMethod, updateMethod = _f === void 0 ? 'append' : _f, propErrorMessages = props.errorMessages, propActionLabel = props.actionLabel, propPlaceholder = props.placeholder, uploadProps = __rest(props, ["accept", "label", "disabled", "helperText", "minSize", "maxSize", "maxFiles", "name", "onChange", "onDeleteClick", "onDrop", "onDropRejected", "defaultValue", "errorState", "isUploading", "className", "style", "id", "action", "updateMethod", "errorMessages", "actionLabel", "placeholder"]);
    var _g = React.useState('empty'), inputState = _g[0], setInputState = _g[1];
    var _h = React.useState(defaultValue || []), files = _h[0], setFiles = _h[1];
    var _j = React.useState(false), isError = _j[0], setError = _j[1];
    var _k = React.useState(null), editingFileIndex = _k[0], setEditingFileIndex = _k[1];
    var multiple = maxFiles > 1;
    var ERROR_MESSAGES = __assign(__assign({}, DEFAULT_ERROR_MESSAGE), propErrorMessages);
    var ACTION_LABELS = __assign(__assign({}, DEFAULT_ACTION_LABELS), propActionLabel);
    var PLACEHOLDERS = __assign(__assign({}, DEFAULT_PLACEHOLDER), propPlaceholder);
    /**
     * check if errorState is set from component props
     */
    React.useEffect(function () {
        if (errorState) {
            setError(true);
            setInputState(errorState);
        }
        else {
            setError(false);
        }
    }, [errorState]);
    /**
     * check if isUploading is set from component props
     * if true, set inputState to uploading
     */
    React.useEffect(function () {
        if (files.length !== 0 && isUploading) {
            setInputState('uploading');
        }
        else if (!isUploading && files.length > 0) {
            setInputState('ok');
        }
    }, [isUploading, files]);
    /**
     * Set new files when default value changes
     * Useful for when the component is rendered but the defaultValue need to change
     * For example when the file is still being fetched
     */
    React.useEffect(function () {
        if (!!defaultValue) {
            setFiles(defaultValue);
        }
    }, [defaultValue]);
    function localOnDrop(acceptedFiles) {
        var IS_UPDATE_REPLACE = updateMethod === 'replace';
        if (multiple) {
            IS_UPDATE_REPLACE = false;
        }
        var selectedFiles = acceptedFiles.map(function (file) {
            return Object.assign(file, {
                preview: URL.createObjectURL(file),
            });
        });
        var previousFiles = files;
        var updatedFiles = [];
        if (IS_UPDATE_REPLACE && acceptedFiles.length >= 1 && !editingFileIndex) {
            previousFiles = [];
            updatedFiles = selectedFiles;
        }
        // is updating a file in multiple mode (and update method is replace)
        else if (editingFileIndex !== null) {
            // should remove the file that is being edited but still preserve the other files and the orders
            updatedFiles = __spreadArray(__spreadArray(__spreadArray([], previousFiles.slice(0, editingFileIndex), true), selectedFiles, true), previousFiles.slice(editingFileIndex + 1), true);
        }
        else {
            // is appending a file (in both single or multiple mode)
            updatedFiles = __spreadArray(__spreadArray([], previousFiles, true), selectedFiles, true);
        }
        if (updatedFiles.length > maxFiles && !IS_UPDATE_REPLACE) {
            propOnFileDropRejected === null || propOnFileDropRejected === void 0 ? void 0 : propOnFileDropRejected('exceedingMaxFiles');
            setError(true);
            setInputState('exceedingMaxFiles');
            return;
        }
        setInputState('ok');
        setEditingFileIndex(null);
        setError(false);
        setFiles(updatedFiles);
        propOnDrop === null || propOnDrop === void 0 ? void 0 : propOnDrop(updatedFiles);
        if (dropzoneRef.current) {
            dropzoneRef.current.files = FileListItems(updatedFiles);
        }
    }
    var onDropRejected = function (dropRejections) {
        var _a, _b, _c;
        var errorCode = (_b = (_a = dropRejections === null || dropRejections === void 0 ? void 0 : dropRejections[0].errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.code;
        var errorType = (_c = ERROR_TYPES[errorCode]) !== null && _c !== void 0 ? _c : 'error';
        propOnFileDropRejected === null || propOnFileDropRejected === void 0 ? void 0 : propOnFileDropRejected(errorType);
        setInputState(errorType);
        setError(true);
    };
    function handleDeleteClick(filename) {
        var newFiles = files.filter(function (file) { return file.name !== filename; });
        newFiles.length === 0 && setInputState('empty');
        setFiles(newFiles);
        propOnDrop === null || propOnDrop === void 0 ? void 0 : propOnDrop(newFiles);
        onDeleteClick === null || onDeleteClick === void 0 ? void 0 : onDeleteClick(filename);
        if (dropzoneRef.current) {
            dropzoneRef.current.files = FileListItems(newFiles);
        }
    }
    function handleEditClick(index) {
        setEditingFileIndex(index !== null && index !== void 0 ? index : null);
        open();
    }
    var _l = useDropzone({
        accept: accept.map(function (t) { return ".".concat(t); }),
        multiple: maxFiles > 1,
        onDrop: localOnDrop,
        onDropRejected: onDropRejected,
        maxSize: maxSize,
        minSize: minSize,
        maxFiles: maxFiles,
        disabled: disabled,
    }), getRootProps = _l.getRootProps, getInputProps = _l.getInputProps, isDragActive = _l.isDragActive, open = _l.open;
    // @ts-expect-error
    var _m = getInputProps({
        refKey: 'dropzoneRef',
        onChange: onChange,
        name: name,
    }), dropzoneRef = _m.dropzoneRef, inputProps = __rest(_m, ["dropzoneRef"]);
    return (React.createElement("div", { className: className, style: style },
        React.createElement("label", { htmlFor: id, style: { display: 'inline-block' } },
            React.createElement(Text, { variant: "body", className: styles.label }, label),
            React.createElement(Text, { variant: "helper", color: "subdued", className: styles.description }, helperText)),
        React.createElement("div", __assign({}, getRootProps({
            className: clsx(isDragActive && styles.dragActive),
        })),
            React.createElement("input", __assign({}, uploadProps, inputProps, { id: id, "data-testid": id, multiple: maxFiles > 1, ref: function (ref) {
                    dropzoneRef.current = ref;
                    if (inputRef) {
                        if (typeof inputRef === 'function') {
                            inputRef(ref);
                        }
                        else {
                            inputRef.current = ref;
                        }
                    }
                } })),
            files.length === 0 && (React.createElement(FileItem, { placeholder: PLACEHOLDERS, isDragActive: isDragActive, isError: isError, isUploading: inputState === 'uploading', isPlaceholder: true, action: action, open: open, actionLabel: ACTION_LABELS }))),
        files.length > 0 &&
            files.map(function (file, index) { return (React.createElement(FileItem, { placeholder: PLACEHOLDERS, key: file.name + index, isDragActive: isDragActive, isError: isError, isUploading: inputState === 'uploading', label: file.name, onDeleteClick: handleDeleteClick, onEditClick: handleEditClick, action: action, actionLabel: ACTION_LABELS, index: index, open: open })); }),
        maxFiles > 1 && files.length > 0 && files.length < maxFiles && (React.createElement(Button, { variant: "ghost", fullWidth: true, onClick: open }, "Tambah File")),
        maxFiles > 1 && files.length >= maxFiles && isError && (React.createElement(Text, { variant: "helper", color: "critical" }, "Anda telah mencapai batas maksimal jumlah upload")),
        isError && inputState && (React.createElement(Text, { variant: "helper", color: "critical" }, (_a = ERROR_MESSAGES[inputState]) !== null && _a !== void 0 ? _a : ''))));
});
Fileupload.displayName = 'Fileupload';
var FileUpload = Fileupload;

export { DEFAULT_ACTION_LABELS, DEFAULT_ERROR_MESSAGE, DEFAULT_PLACEHOLDER, ERROR_TYPES, FileItem, FileUpload, Fileupload };
